import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { WA_WINDOW } from '@ng-web-apis/common';
import { type ProfileType, VerticalType } from '@rma/common-ui-domain/types';
import { LocationType, PagedList } from '@rma/generic/domain/types';
import { RmaUrlsService } from '@rma/generic/util/environment';
import { httpParamMapper } from '@rma/generic/util/http';
import {
  DetailedSearchReview,
  RandomReviewFilters,
  RecentReview,
  ReviewAd,
  type ReviewSearchFilter,
  type ReviewsPagedList,
  type SearchReview,
} from '@rma/reviews/public/domain';
import { map, Observable } from 'rxjs';

export function addDefaultSearchReviewFilters<T>(filters: T): T {
  return { statuses: ['Active', 'Requested'], ...filters, isSubmitted: true };
}

@Injectable({
  providedIn: 'root',
})
export class ReviewApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly rmaUrls = inject(RmaUrlsService);

  private readonly window = inject(WA_WINDOW);

  /**
   * New agnostic reviews service
   * @param filter
   * @returns
   */
  public searchReviews(filter: ReviewSearchFilter): Observable<PagedList<SearchReview>> {
    return this.httpClient.post<ReviewsPagedList<SearchReview>>(
      this.rmaUrls.apiUrl('reviews/search/profileDetails', 'Reviews'),
      addDefaultSearchReviewFilters(filter),
    );
  }

  public getReviews(filter: ReviewSearchFilter): Observable<ReviewsPagedList<SearchReview>> {
    return this.searchReviewsWithAggregations(filter);
  }

  public getDetailedReviews(filter: ReviewSearchFilter): Observable<PagedList<DetailedSearchReview>> {
    return this.httpClient.post<PagedList<DetailedSearchReview>>(
      this.rmaUrls.apiUrl('reviews/search/full', 'Reviews'),
      addDefaultSearchReviewFilters(filter),
    );
  }

  public getReviewByCode(reviewCode: string, verticalType?: VerticalType, request?: ReviewSearchFilter): Observable<DetailedSearchReview> {
    //Defending against an empty review code as review code can come from route param
    if (!reviewCode || reviewCode === '') {
      throw new Error('A review code must be passed');
    }
    // if we only need a the vertical type for a legacy review code
    // Can be removed when we get rid of legacy url codes
    const filter: ReviewSearchFilter = verticalType
      ? { ...request, legacyReviewCodes: [reviewCode], verticalTypes: [verticalType] }
      : { ...request, reviewCodes: [reviewCode] };

    return this.getDetailedReviews(filter).pipe(
      map((resp) => {
        if (!resp.results.length) {
          const url = this.window.document.URL;
          throw new HttpErrorResponse({ status: 404, statusText: 'Not Found', url });
        }
        return resp.results[0] as DetailedSearchReview;
      }),
    );
  }

  public getReviewsForCampaign(verticalType: VerticalType, profileType: ProfileType, campaignCode: string): Observable<SearchReview[]> {
    return this.httpClient.get<SearchReview[]>(
      this.rmaUrls.apiUrl(`${verticalType}/${profileType}/Campaign/${campaignCode}/Reviews`, 'Reviews'),
    );
  }

  public getRecentReviews(
    verticalType: VerticalType,
    profileType: ProfileType,
    locationType: LocationType,
    locationId: number,
  ): Observable<RecentReview[]> {
    const options = { params: httpParamMapper({ locationType, locationId, take: 10, isSubmitted: true }) };
    return this.httpClient.get<RecentReview[]>(this.rmaUrls.apiUrl(`${verticalType}/${profileType}/RecentReviews`, 'Reviews'), options);
  }

  public getRandomMbReview(filter?: RandomReviewFilters): Observable<ReviewAd[]> {
    const url = this.rmaUrls.apiUrl('review/MortgageBroker/random', 'Reviews');
    return this.httpClient.post<{ result: ReviewAd[] }>(url, filter).pipe(map((x) => x.result));
  }

  public getSingleReviewPdfDownloadLink(reviewCode: string): Observable<Blob> {
    const endPoint = this.rmaUrls.apiUrl('MarketingReports/SingleReview/pdf');
    return this.httpClient.get(endPoint, { params: { reviewCode }, withCredentials: true, responseType: 'blob' });
  }

  private searchReviewsWithAggregations(filter: ReviewSearchFilter): Observable<ReviewsPagedList<SearchReview>> {
    return this.httpClient.post<ReviewsPagedList<SearchReview>>(
      this.rmaUrls.apiUrl('reviews/search/WithAggregations', 'Reviews'),
      addDefaultSearchReviewFilters(filter),
    );
  }
}
